/**
 * Queue building and Session state transitions (BUILD-SPEC.md §4).
 *
 * All transitions are pure: `(Session, args) => Session`. None of them
 * mutate their input, and none of them read a clock — `now` always comes
 * in as a parameter from the caller.
 */

import type { QueueEntry, Session, Settings } from "./types";
import { elapsedFor } from "./timing";

/**
 * Fisher–Yates shuffle. Pure: returns a new array, never mutates `arr`.
 * `rng` is injectable for deterministic tests; defaults to `Math.random`.
 */
export function shuffle<T>(arr: readonly T[], rng: () => number = Math.random): T[] {
  const result = arr.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = result[i] as T;
    result[i] = result[j] as T;
    result[j] = tmp;
  }
  return result;
}

let idCounter = 0;
/** Generates a stable-enough unique suffix for manual entry keys, without
 * touching the clock. Not cryptographically unique — just unique within a
 * running process, which is all a queue key needs. */
function nextManualId(): string {
  idCounter += 1;
  return `${idCounter}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Builds a fresh QueueEntry in the "waiting" state. */
function makeEntry(args: {
  key: string;
  userId: string | null;
  displayName: string;
  source: "invited" | "manual";
  joinedLate: boolean;
}): QueueEntry {
  return {
    key: args.key,
    userId: args.userId,
    displayName: args.displayName,
    source: args.source,
    status: "waiting",
    joinedLate: args.joinedLate,
    spentSeconds: 0,
    extensionSeconds: 0,
  };
}

/**
 * Starts the standup. `order` is the already-shuffled list of checked
 * entries (shuffling is the caller's job — see `shuffle` above). The first
 * entry becomes the speaker; every other entry is reset to "waiting".
 *
 * `settings` is the current (possibly just-edited) persisted settings —
 * copied into `limitSeconds`/`graceSeconds` here so a slider change made on
 * the tick list right before pressing Start is honored, rather than
 * whatever settings the session happened to be constructed with.
 *
 * An empty `order` goes straight to "finished" rather than crashing on an
 * out-of-range index (see BUILD-SPEC.md §9.10: "One person, or zero, in the
 * list -> no crash").
 */
export function start(
  s: Session,
  args: { order: QueueEntry[]; settings: Settings; now: number; by: string }
): Session {
  const { order, settings, now, by } = args;

  if (order.length === 0) {
    return {
      ...s,
      phase: "finished",
      queue: [],
      idx: 0,
      limitSeconds: settings.secondsPerPerson,
      graceSeconds: settings.graceSeconds,
      turnStartedAt: null,
      zeroPointAt: null,
      lastActionBy: by,
      lastActionAt: now,
    };
  }

  const queue = order.map((entry, i) => ({
    ...entry,
    status: i === 0 ? ("speaking" as const) : ("waiting" as const),
  }));

  return {
    ...s,
    phase: "running",
    queue,
    idx: 0,
    limitSeconds: settings.secondsPerPerson,
    graceSeconds: settings.graceSeconds,
    turnStartedAt: now,
    zeroPointAt: null,
    lastActionBy: by,
    lastActionAt: now,
  };
}

/**
 * Advances to the next speaker, skipping any entries whose status is
 * "left". Banks the outgoing speaker's elapsed time into `spentSeconds` and
 * marks them "done". Advancing past the last entry moves to "finished".
 *
 * Double-advance guard: `args.expectedIdx` must match `s.idx` or the call is
 * dropped entirely (returns `s` unchanged) — this is what stops a
 * double-click (or two people clicking at once) from skipping two speakers.
 */
export function next(
  s: Session,
  args: { expectedIdx: number; now: number; by: string }
): Session {
  const { expectedIdx, now, by } = args;

  if (expectedIdx !== s.idx) {
    return s; // stale call, dropped
  }

  const current = s.queue[s.idx];
  if (!current) {
    return s;
  }

  const bankedElapsed = elapsedFor(s, s.idx, now);

  const queueAfterBank = s.queue.map((entry, i) =>
    i === s.idx
      ? { ...entry, status: "done" as const, spentSeconds: bankedElapsed }
      : entry
  );

  let nextIdx = -1;
  for (let i = s.idx + 1; i < queueAfterBank.length; i++) {
    if (queueAfterBank[i]?.status !== "left") {
      nextIdx = i;
      break;
    }
  }

  if (nextIdx === -1) {
    return {
      ...s,
      queue: queueAfterBank,
      idx: queueAfterBank.length,
      phase: "finished",
      turnStartedAt: null,
      zeroPointAt: null,
      lastActionBy: by,
      lastActionAt: now,
    };
  }

  const queue = queueAfterBank.map((entry, i) =>
    i === nextIdx ? { ...entry, status: "speaking" as const } : entry
  );

  return {
    ...s,
    queue,
    idx: nextIdx,
    phase: "running",
    turnStartedAt: now,
    zeroPointAt: null,
    lastActionBy: by,
    lastActionAt: now,
  };
}

/** Pauses the running turn: banks elapsed time so far, stops the clock. */
export function pause(s: Session, args: { now: number; by: string }): Session {
  const { now, by } = args;

  if (s.phase !== "running") {
    return s;
  }

  const current = s.queue[s.idx];
  if (!current) {
    return { ...s, phase: "paused", turnStartedAt: null, lastActionBy: by, lastActionAt: now };
  }

  const bankedElapsed = elapsedFor(s, s.idx, now);
  const queue = s.queue.map((entry, i) =>
    i === s.idx ? { ...entry, spentSeconds: bankedElapsed } : entry
  );

  return {
    ...s,
    queue,
    phase: "paused",
    turnStartedAt: null,
    lastActionBy: by,
    lastActionAt: now,
  };
}

/** Resumes a paused turn from its banked spentSeconds. */
export function resume(s: Session, args: { now: number; by: string }): Session {
  const { now, by } = args;

  if (s.phase !== "paused") {
    return s;
  }

  return {
    ...s,
    phase: "running",
    turnStartedAt: now,
    lastActionBy: by,
    lastActionAt: now,
  };
}

/** Grants extra time to the current speaker, cumulative. */
export function extend(
  s: Session,
  args: { seconds: number; now: number; by: string }
): Session {
  const { seconds, now, by } = args;

  const current = s.queue[s.idx];
  if (!current) {
    return s;
  }

  const queue = s.queue.map((entry, i) =>
    i === s.idx
      ? { ...entry, extensionSeconds: entry.extensionSeconds + seconds }
      : entry
  );

  return { ...s, queue, lastActionBy: by, lastActionAt: now };
}

/** Appends a typed (non-roster) name to the end of the queue. */
export function addManual(
  s: Session,
  args: { displayName: string; now: number; by: string }
): Session {
  const { displayName, now, by } = args;

  const entry = makeEntry({
    key: `manual:${nextManualId()}`,
    userId: null,
    displayName,
    source: "manual",
    joinedLate: true,
  });

  return {
    ...s,
    queue: [...s.queue, entry],
    lastActionBy: by,
    lastActionAt: now,
  };
}

/** Appends a roster (invited) member to the end of the queue. */
export function addInvited(
  s: Session,
  args: { userId: string; displayName: string; now: number; by: string }
): Session {
  const { userId, displayName, now, by } = args;

  const entry = makeEntry({
    key: userId,
    userId,
    displayName,
    source: "invited",
    joinedLate: true,
  });

  return {
    ...s,
    queue: [...s.queue, entry],
    lastActionBy: by,
    lastActionAt: now,
  };
}

/**
 * Marks the entry for `key` as having left. If it was the current speaker,
 * the clock stops and the elapsed time *this turn* is discarded — only the
 * previously banked `spentSeconds` survives, so a dropped connection never
 * costs anyone time they never used. If several entries share this key
 * (from a previous rejoin), the most recent still-active one (searching
 * from the end) is the one marked "left"; entries already "done" or "left"
 * are untouched.
 */
export function markLeft(s: Session, args: { key: string; now: number }): Session {
  const { key } = args;

  let targetIndex = -1;
  for (let i = s.queue.length - 1; i >= 0; i--) {
    const entry = s.queue[i];
    if (entry && entry.key === key && (entry.status === "waiting" || entry.status === "speaking")) {
      targetIndex = i;
      break;
    }
  }

  if (targetIndex === -1) {
    return s; // nothing active to mark left (unknown key, or already done/left)
  }

  const wasCurrentSpeaker =
    targetIndex === s.idx && s.queue[targetIndex]?.status === "speaking";

  const queue = s.queue.map((entry, i) =>
    i === targetIndex ? { ...entry, status: "left" as const } : entry
  );

  if (!wasCurrentSpeaker) {
    return { ...s, queue };
  }

  // Discard partial time: do NOT bank the live elapsed portion of this
  // turn. spentSeconds keeps whatever was already banked (e.g. 0, for a
  // speaker who never paused before dropping).
  return {
    ...s,
    queue,
    turnStartedAt: null,
    zeroPointAt: null,
  };
}

/**
 * A person reconnecting. Per BUILD-SPEC.md §4:
 *
 * - If their most recent entry is "done", do nothing — no second turn.
 * - Otherwise (their most recent entry is "left", or they have no previous
 *   entry at all), append a brand-new entry at the end with
 *   `spentSeconds: 0`. The old entry (if any) stays visible as "left".
 *   The same person may legitimately appear twice; entries are never
 *   deduplicated.
 */
export function rejoin(
  s: Session,
  args: { userId: string; displayName: string; now: number }
): Session {
  const { userId, displayName } = args;

  let lastIndex = -1;
  for (let i = s.queue.length - 1; i >= 0; i--) {
    if (s.queue[i]?.userId === userId) {
      lastIndex = i;
      break;
    }
  }

  if (lastIndex !== -1 && s.queue[lastIndex]?.status === "done") {
    return s; // already had their turn — no second turn
  }

  const entry = makeEntry({
    key: userId,
    userId,
    displayName,
    source: "invited",
    joinedLate: true,
  });

  return {
    ...s,
    queue: [...s.queue, entry],
  };
}

/** Ends the standup. Banks any in-progress speaking time and moves to
 * "finished". */
export function end(s: Session, args: { now: number; by: string }): Session {
  const { now, by } = args;

  const current = s.queue[s.idx];
  const queue =
    current && current.status === "speaking"
      ? s.queue.map((entry, i) =>
          i === s.idx
            ? { ...entry, status: "done" as const, spentSeconds: elapsedFor(s, s.idx, now) }
            : entry
        )
      : s.queue;

  return {
    ...s,
    queue,
    phase: "finished",
    turnStartedAt: null,
    zeroPointAt: null,
    lastActionBy: by,
    lastActionAt: now,
  };
}

/**
 * Clears the standup and returns to the tick list.
 *
 * This is what "New standup" on the finished screen does. It must be a real
 * transition on the shared session rather than something a single screen
 * decides for itself: otherwise the person who clicked it sees a tick list
 * while everybody else still sees "Standup complete", and pressing Start
 * drags the group into a new standup with no warning.
 *
 * Settings survive, because they are the only thing this app remembers.
 * Everything about the people is dropped.
 */
export function reset(s: Session, args: { now: number; by: string }): Session {
  const { now, by } = args;
  return {
    ...s,
    queue: [],
    idx: 0,
    phase: "ticklist",
    turnStartedAt: null,
    zeroPointAt: null,
    lastActionBy: by,
    lastActionAt: now,
  };
}
